'use client';

import React, { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useStore } from 'react-redux';
import { useCsrfContext } from './CsrfProvider';
import { authLogger } from '../utils/logger';

interface AppInitializationContextType {
  isReady: boolean;
  isReduxReady: boolean;
  isCsrfReady: boolean;
  error: string | null;
  waitUntilReady: () => Promise<void>;
}

const AppInitializationContext = createContext<AppInitializationContextType>({
  isReady: false,
  isReduxReady: false,
  isCsrfReady: false,
  error: null,
  waitUntilReady: () => Promise.resolve()
});

export const useAppInitialization = () => useContext(AppInitializationContext);

interface AppInitializationProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that tracks when all critical app systems are initialized
 * This ensures operations don't happen before dependencies are ready
 */
export function AppInitializationProvider({ children }: AppInitializationProviderProps) {
  const [isReduxReady, setIsReduxReady] = useState(false);
  const store = useStore();
  const { csrfToken, isLoading: csrfLoading, error: csrfError } = useCsrfContext();
  const readyPromiseRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
  } | null>(null);
  
  // Check Redux store initialization
  useEffect(() => {
    if (!store || isReduxReady) return;

    const checkStore = (): boolean => {
      try {
        const state = store.getState();
        if (state && Object.keys(state).length > 0) {
          setIsReduxReady(true);
          authLogger.debug('[AppInitialization] Redux store ready');
          return true;
        }
        return false;
      } catch (error) {
        authLogger.error('[AppInitialization] Error checking Redux store:', error);
        return false;
      }
    };

    // Check immediately
    if (!checkStore()) {
      // Retry once after a short delay (Redux may still be initializing)
      const timeoutId = setTimeout(() => {
        checkStore();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [store, isReduxReady]);
  
  // CSRF is ready when token exists or there's an error (don't wait forever)
  const isCsrfReady = !csrfLoading && (!!csrfToken || !!csrfError);
  
  // Overall readiness
  const isReady = isReduxReady && isCsrfReady;
  
  // Determine error state
  let error: string | null = null;
  if (csrfError && !csrfToken) {
    error = 'Security initialization failed';
  }
  
  // Log when fully ready
  useEffect(() => {
    if (isReady) {
      authLogger.info('[AppInitialization] App fully initialized', {
        redux: isReduxReady,
        csrf: isCsrfReady,
        hasToken: !!csrfToken
      });
      
      // Resolve any waiting promises
      if (readyPromiseRef.current) {
        readyPromiseRef.current.resolve();
        readyPromiseRef.current = null;
      }
    }
  }, [isReady, isReduxReady, isCsrfReady, csrfToken]);
  
  // Handle errors
  useEffect(() => {
    if (error && readyPromiseRef.current) {
      readyPromiseRef.current.reject(new Error(error));
      readyPromiseRef.current = null;
    }
  }, [error]);
  
  /**
   * Returns a promise that resolves when the app is ready
   * or rejects if there's an error or timeout
   */
  const waitUntilReady = useCallback(async (): Promise<void> => {
    // If already ready, resolve immediately
    if (isReady) {
      return Promise.resolve();
    }

    // If there's already a promise waiting, return it
    if (readyPromiseRef.current) {
      return readyPromiseRef.current.promise;
    }

    // Create a new promise with deferred resolve/reject
    let resolve: (() => void) | undefined;
    let reject: ((error: Error) => void) | undefined;

    const promise = new Promise<void>((res, rej) => {
      reject = rej;

      // Set up timeout - increased to 30 seconds for slow networks/cold starts
      const timeoutId = setTimeout(() => {
        authLogger.warn('[AppInitialization] Initialization timeout after 30 seconds', {
          isReduxReady,
          isCsrfReady,
          csrfError: !!csrfError
        });
        rej(new Error('App initialization timeout after 30 seconds. Please check your network connection and try again.'));
        readyPromiseRef.current = null;
      }, 30000);

      // Clear timeout when resolved
      resolve = () => {
        clearTimeout(timeoutId);
        res();
      };
    });

    // resolve and reject are guaranteed to be set by the synchronous Promise constructor
    readyPromiseRef.current = { promise, resolve: resolve as () => void, reject: reject as (error: Error) => void };

    // Check if we became ready while setting up the promise
    if (isReady) {
      readyPromiseRef.current.resolve();
      readyPromiseRef.current = null;
    }

    return promise;
  }, [isReady, isReduxReady, isCsrfReady, csrfError]);

  // Memoize context value to prevent cascading re-renders
  const value = useMemo<AppInitializationContextType>(
    () => ({ isReady, isReduxReady, isCsrfReady, error, waitUntilReady }),
    [isReady, isReduxReady, isCsrfReady, error, waitUntilReady],
  );

  return (
    <AppInitializationContext.Provider value={value}>
      {children}
    </AppInitializationContext.Provider>
  );
}